import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GET_BUYBACK_AGREEMENT, post } from '@services';
import StatusChip from '@components/widgets/StatusChip';
import Loader from '@components/widgets/Loader';
import { format, parseISO } from 'date-fns';

import { AgreementDetail } from '@types';
import { formatCurrency, formatKilometers } from '@utils';

const AgreementDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const [agreement, setAgreement] = useState<AgreementDetail | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchAgreementDetail(id);
    }
  }, [id]);

  const fetchAgreementDetail = async (buybackId: string) => {
    try {
      setLoading(true);
      const response = await post(GET_BUYBACK_AGREEMENT, { buybackId });
      if (response.agreement) {
        setAgreement(response.agreement);
      } else {
        setError('Agreement not found');
      }
    } catch (err) {
      console.error('Error fetching agreement details:', err);
      setError('Failed to load agreement details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in life':
        return 'success';
      case 'quoted':
        return 'warning';
      case 'completed':
        return 'success1';
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };

  const handleMakeOffer = () => {
    // Demo functionality - in real implementation this would open a modal or navigate to offer form
    alert(
      `Making an offer for Agreement ${agreement?.friendlyId}\nCurrent Buyback Amount: ${formatCurrency(agreement?.buybackAmount || 0)}\n\nThis would typically open an offer form or modal.`
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error || !agreement) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
        <p className="text-grey-600">{error || 'Agreement not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-semibold text-gray-900">
                Agreement {agreement.friendlyId}
              </h1>
              <StatusChip
                variant={getStatusVariant(agreement.status)}
                label={agreement.status}
              />
            </div>

            {/* Vehicle Info Card */}
            <div className="inline-block min-w-[250px]">
              {/* Vehicle Card */}
              <div className="bg-gray-900 text-white p-4 rounded-lg flex items-center w-full">
                <div className="w-16 h-16 bg-gray-700 text-white rounded flex items-center justify-center mr-4">
                  <span className="font-bold text-sm">JAECOO</span>
                </div>
                <div>
                  <h3 className="font-semibold">
                    {agreement.vehicle.model} {agreement.vehicle.variant}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {agreement.vehicle.year}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Agreement Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-6">Agreement Summary</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Buyback Amount</p>
              <p className="font-semibold text-lg text-green-600">
                {formatCurrency(agreement.buybackAmount)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">VIN</p>
              <p className="font-semibold font-mono">{agreement.vin || ''}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Start Date</p>
              <p className="font-semibold">
                {format(parseISO(agreement.startDate), 'd MMM yyyy')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">End Date</p>
              <p className="font-semibold">
                {format(parseISO(agreement.endDate), 'd MMM yyyy')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Term</p>
              <p className="font-semibold">{agreement.termMonths} months</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Total KMs</p>
              <p className="font-semibold">
                {formatKilometers(agreement.totalKms)}
              </p>
            </div>
          </div>
        </div>

        {/* Vehicle Information & Specifications */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-6">
            Vehicle Information & Specifications
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Body Style</p>
              <p className="font-semibold">
                {agreement.vehicle.bodystyle || ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">CO2 Combined</p>
              <p className="font-semibold">
                {agreement.vehicle.co2Combined
                  ? `${agreement.vehicle.co2Combined}g/km`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Doors</p>
              <p className="font-semibold">{agreement.vehicle.doors || ''}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Drivetrain</p>
              <p className="font-semibold">
                {agreement.vehicle.drivetrain || ''}
              </p>
            </div>

            <div>
              <p className="text-gray-600 mb-1">Engine</p>
              <p className="font-semibold">
                {agreement.vehicle.engineSize || ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Engine Power</p>
              <p className="font-semibold">
                {agreement.vehicle.enginePower
                  ? `${agreement.vehicle.enginePower} kW`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Engine Torque</p>
              <p className="font-semibold">
                {agreement.vehicle.engineTorque
                  ? `${agreement.vehicle.engineTorque} Nm`
                  : ''}
              </p>
            </div>
            {agreement.vehicle.evType && (
              <div>
                <p className="text-gray-600 mb-1">EV Type</p>
                <p className="font-semibold">{agreement.vehicle.evType}</p>
              </div>
            )}

            <div>
              <p className="text-gray-600 mb-1">Fuel Code</p>
              <p className="font-semibold">
                {agreement.vehicle.fuelCode || ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Fuel Combined</p>
              <p className="font-semibold">
                {agreement.vehicle.fuelCombined
                  ? `${agreement.vehicle.fuelCombined}L/100km`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Fuel Tank</p>
              <p className="font-semibold">
                {agreement.vehicle.fuelTank
                  ? `${agreement.vehicle.fuelTank}L`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Fuel Type</p>
              <p className="font-semibold">
                {agreement.vehicle.fuelType || ''}
              </p>
            </div>

            <div>
              <p className="text-gray-600 mb-1">Gross Vehicle Mass</p>
              <p className="font-semibold">
                {agreement.vehicle.grossVehicleMass
                  ? `${agreement.vehicle.grossVehicleMass}kg`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Release Date</p>
              <p className="font-semibold">
                {agreement.vehicle.releaseDate
                  ? format(
                      parseISO(agreement.vehicle.releaseDate),
                      'd MMM yyyy'
                    )
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Seats</p>
              <p className="font-semibold">{agreement.vehicle.seats || ''}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Series</p>
              <p className="font-semibold">{agreement.vehicle.series || ''}</p>
            </div>

            <div>
              <p className="text-gray-600 mb-1">Towing Mass</p>
              <p className="font-semibold">
                {agreement.vehicle.towingMass
                  ? `${agreement.vehicle.towingMass}kg`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Transmission</p>
              <p className="font-semibold">
                {agreement.vehicle.transmission || ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Vehicle Mass</p>
              <p className="font-semibold">
                {agreement.vehicle.vehicleMass
                  ? `${agreement.vehicle.vehicleMass}kg`
                  : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Wheel Base</p>
              <p className="font-semibold">
                {agreement.vehicle.wheelBase
                  ? `${agreement.vehicle.wheelBase}mm`
                  : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Pricing and Warranty Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pricing Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Pricing Information</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Price New:</span>
                <span className="font-semibold">
                  {agreement.vehicle.priceNew
                    ? formatCurrency(agreement.vehicle.priceNew)
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price High:</span>
                <span className="font-semibold">
                  {agreement.vehicle.priceHigh
                    ? formatCurrency(agreement.vehicle.priceHigh)
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price Mid:</span>
                <span className="font-semibold">
                  {agreement.vehicle.priceMid
                    ? formatCurrency(agreement.vehicle.priceMid)
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price Low:</span>
                <span className="font-semibold">
                  {agreement.vehicle.priceLow
                    ? formatCurrency(agreement.vehicle.priceLow)
                    : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Warranty Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Warranty & Service</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Warranty:</span>
                <span className="font-semibold">
                  {agreement.vehicle.warrantyMonths &&
                  agreement.vehicle.warrantyKms
                    ? `${agreement.vehicle.warrantyMonths} months / ${formatKilometers(agreement.vehicle.warrantyKms)}`
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service Interval:</span>
                <span className="font-semibold">
                  {agreement.vehicle.serviceMonths &&
                  agreement.vehicle.serviceKms
                    ? `${agreement.vehicle.serviceMonths} months / ${formatKilometers(agreement.vehicle.serviceKms)}`
                    : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementDetailView;
